import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Gift, Unlock } from "lucide-react";
import { toast } from "sonner";
import {
  getCharacterResponse, CHARACTER_EXTRAS, WORLD_EXTRAS, MOOD_WORDS, worldOfCharacter,
} from "@/data";
import { useGame } from "@/context/GameContext";
import { sound } from "@/lib/sound";
import { Icon } from "@/lib/iconMap";

const clamp = (n) => Math.max(0, Math.min(100, n));
const moodInfo = (m) =>
  m >= 67
    ? { label: "Happy", labelKr: "기분 좋음", color: "#34d399" }
    : m >= 34
    ? { label: "Neutral", labelKr: "보통", color: "#fbbf24" }
    : { label: "Suspicious", labelKr: "의심", color: "#f87171" };

function detectName(text) {
  const m = text.match(/(?:my name is|i am|i'?m|call me)\s+([A-Za-z][A-Za-z'’-]{1,18})/i);
  if (!m) return null;
  const stop = ["fine", "good", "okay", "ok", "not", "so", "sorry", "here", "just", "really", "very", "happy", "sad", "tired", "back", "sure"];
  const name = m[1];
  if (stop.includes(name.toLowerCase())) return null;
  return name.charAt(0).toUpperCase() + name.slice(1);
}

const WARM = [
  "*a warm smile* That's kind of you to say. Thank you.",
  "Oh—! *a little flustered* You really think so? Thank you.",
  "*pleased* Mm. Flattery suits you. Go on.",
];
const COOL = [
  "*frowns* …That was uncalled for.",
  "*stiffens* I'll pretend I didn't hear that.",
  "*coldly* Careful.",
];
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const ChatPanel = ({ character, world, onClose }) => {
  const ex = CHARACTER_EXTRAS[character.id];
  const { state, setName, addChat, unlockSecret, givePerfectGift } = useGame();
  const worldId = world?.id || worldOfCharacter(character.id);
  const gifts = WORLD_EXTRAS[worldId].gifts;

  const [messages, setMessages] = useState([{ sender: "character", text: character.greeting }]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [mood, setMood] = useState(50);
  const [secretUnlocked, setSecretUnlocked] = useState(!!state.secrets[character.id]);
  const [giftOpen, setGiftOpen] = useState(false);
  const [delta, setDelta] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, typing]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const pushReply = (text, opts = {}) => {
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages((m) => [...m, { sender: "character", text, ...opts }]);
    }, 700);
  };

  const applyDelta = (d, base) => {
    if (d !== 0) {
      const key = Date.now();
      setDelta({ v: d, key });
      setTimeout(() => setDelta((cur) => (cur && cur.key === key ? null : cur)), 900);
    }
    return clamp(base + d);
  };

  const handleSend = (raw) => {
    const text = (raw ?? input).trim();
    if (!text) return;
    setMessages((m) => [...m, { sender: "user", text }]);
    setInput("");
    addChat(character.id);

    const name = detectName(text);
    if (name) setName(name);

    const lower = text.toLowerCase();
    const isCompliment =
      MOOD_WORDS.compliments.some((w) => lower.includes(w)) || text === ex.quick.compliment;
    const isRude = MOOD_WORDS.rude.some((w) => lower.includes(w));
    const isProbe = MOOD_WORDS.probe.some((w) => lower.includes(w)) || text === ex.quick.secretAsk;
    let d = 0;
    if (isCompliment) d += 12;
    if (isRude) d -= 18;
    if (isProbe) d += ex.secretProbe;
    const newMood = applyDelta(d, mood);
    setMood(newMood);

    // memory recall
    if (/what'?s my name|remember my name|who am i|do you remember me/i.test(text)) {
      const n = state.playerName || name;
      pushReply(n ? `Of course — you're ${n}. How could I forget?` : "*tilts head* You haven't told me your name yet.");
      return;
    }
    if (name) {
      pushReply(`"${name}" — what a lovely name. I'll remember it.`);
      return;
    }

    // secret reveal logic
    if (isProbe) {
      if (secretUnlocked) {
        pushReply(ex.secret, { secret: true });
      } else if (newMood >= 67) {
        setSecretUnlocked(true);
        unlockSecret(character.id);
        toast("🔓 Secret unlocked", { description: `${character.name}'s secret` });
        pushReply(ex.secret, { secret: true });
      } else {
        pushReply(ex.secretTease);
      }
      return;
    }

    if (isCompliment) {
      pushReply(pick(WARM));
      return;
    }
    if (isRude) {
      pushReply(pick(COOL));
      return;
    }
    pushReply(getCharacterResponse(character, text));
  };

  const giveGift = (g) => {
    setGiftOpen(false);
    setMessages((m) => [...m, { sender: "user", text: `*hands over a ${g.name}*`, gift: true }]);
    sound.playGift();
    if (g.id === ex.perfectGift) {
      givePerfectGift(character.id, g.id);
      setMood(applyDelta(35, mood));
      pushReply(ex.giftReaction);
    } else {
      setMood(applyDelta(6, mood));
      pushReply("*accepts it with a polite nod* Thank you… that's kind of you.");
    }
  };

  const chips = (() => {
    const set = [ex.quick.compliment];
    if (secretUnlocked) set.push("Tell me more about that.");
    else if (mood >= 67) set.push(ex.quick.secretAsk);
    else set.push(ex.quick.topics[0]);
    set.push(mood < 34 ? ex.quick.topics[1] : ex.quick.topics[2] || ex.quick.topics[1]);
    return [...new Set(set)].slice(0, 3);
  })();

  const mi = moodInfo(mood);

  return (
    <motion.aside
      className="wv-chat fixed inset-y-0 right-0 z-[100] flex w-full max-w-md flex-col border-l border-white/10 bg-black/70 backdrop-blur-2xl"
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "spring", stiffness: 260, damping: 30 }}
      data-testid="chat-panel"
      style={{ "--accent": character.accent }}
    >
      {/* header */}
      <div className="border-b border-white/10 p-5">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 shrink-0 overflow-hidden rounded-full border-2" style={{ borderColor: character.accent }}>
            {character.avatar ? (
              <img src={character.avatar} alt={character.name} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center font-display text-xl text-white" style={{ background: `linear-gradient(135deg, ${character.accent}, rgba(0,0,0,0.6))` }}>
                {character.name.charAt(0)}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-lg tracking-tight text-white">
              {character.name}{" "}
              <span className="kr text-sm font-normal" style={{ color: character.accent }}>
                {character.koreanName}
              </span>
            </h3>
            <p className="truncate text-xs text-white/55">{character.role}</p>
          </div>
          <button
            onClick={onClose}
            data-testid="chat-close"
            className="rounded-full p-2 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* mood meter */}
        <div className="relative mt-4" data-testid="mood-meter">
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="text-white/50">Mood</span>
            <span className="flex items-center gap-1.5 font-medium" style={{ color: mi.color }} data-testid="mood-label">
              {mi.label} <span className="kr font-normal text-white/40">{mi.labelKr}</span>
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-white/10">
            <motion.div
              className="h-full rounded-full"
              style={{ background: mi.color }}
              animate={{ width: `${mood}%` }}
              transition={{ type: "spring", stiffness: 200, damping: 24 }}
            />
          </div>
          <AnimatePresence>
            {delta && (
              <motion.span
                key={delta.key}
                className="absolute right-0 top-5 text-xs font-bold"
                style={{ color: delta.v > 0 ? "#34d399" : "#f87171" }}
                initial={{ opacity: 0, y: 0 }}
                animate={{ opacity: 1, y: -4 }}
                exit={{ opacity: 0, y: -14 }}
              >
                {delta.v > 0 ? `+${delta.v}` : delta.v}
              </motion.span>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* messages */}
      <div ref={scrollRef} className="wv-scroll flex-1 space-y-4 overflow-y-auto p-5">
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}
            data-testid={`chat-message-${m.sender}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                m.sender === "user"
                  ? "rounded-br-sm bg-white/90 text-black"
                  : "rounded-bl-sm border bg-white/5 text-white/90"
              } ${m.gift ? "italic text-white/70" : ""}`}
              style={
                m.sender === "character"
                  ? {
                      borderColor: m.secret ? character.accent : `${character.accent}55`,
                      background: m.secret ? `${character.accent}1a` : undefined,
                    }
                  : undefined
              }
            >
              {m.secret && (
                <span className="mb-1 flex items-center gap-1 text-xs font-semibold" style={{ color: character.accent }}>
                  <Unlock className="h-3 w-3" /> Secret revealed
                </span>
              )}
              {m.text}
            </div>
          </div>
        ))}
        {typing && (
          <div className="flex justify-start" data-testid="chat-typing">
            <div className="flex items-center gap-1 rounded-2xl rounded-bl-sm border border-white/10 bg-white/5 px-4 py-3">
              {[0, 1, 2].map((dd) => (
                <motion.span
                  key={dd}
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: character.accent }}
                  animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
                  transition={{ duration: 0.9, repeat: Infinity, delay: dd * 0.15 }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* contextual quick replies */}
      <div className="flex flex-wrap gap-2 px-5 pb-2">
        {chips.map((s) => (
          <button
            key={s}
            onClick={() => handleSend(s)}
            data-testid="chat-suggestion"
            className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white/70 transition-colors hover:border-white/40 hover:text-white"
          >
            {s}
          </button>
        ))}
      </div>

      {/* gift menu */}
      <AnimatePresence>
        {giftOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="mx-5 mb-2 rounded-2xl border border-white/10 bg-black/60 p-3"
            data-testid="gift-menu"
          >
            <p className="mb-2 text-xs text-white/50">Give a gift 🎁</p>
            <div className="flex gap-2">
              {gifts.map((g) => (
                <button
                  key={g.id}
                  onClick={() => giveGift(g)}
                  data-testid={`gift-${g.id}`}
                  className="flex flex-1 flex-col items-center gap-1 rounded-xl border border-white/10 bg-white/5 p-2 transition-colors hover:bg-white/10"
                >
                  <Icon name={g.icon} className="h-5 w-5" style={{ color: character.accent }} />
                  <span className="text-[0.6rem] text-white/70">{g.name}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* input row */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
        className="flex items-center gap-2 border-t border-white/10 p-4 pb-20"
      >
        <button
          type="button"
          onClick={() => {
            sound.playClick();
            setGiftOpen((o) => !o);
          }}
          data-testid="gift-button"
          aria-label="Give a gift"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-white/70 transition-colors hover:text-white"
          style={{ borderColor: `${character.accent}55` }}
        >
          <Gift className="h-4 w-4" />
        </button>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={`Message ${character.name}…`}
          data-testid="chat-input"
          className="flex-1 rounded-full border border-white/15 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-white/35 focus:border-white/40 focus:outline-none"
        />
        <button
          type="submit"
          data-testid="chat-send"
          className="flex h-10 w-10 items-center justify-center rounded-full text-black transition-transform hover:scale-105"
          style={{ background: character.accent }}
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </motion.aside>
  );
};

export default ChatPanel;
